// ---------------------------------------------------------------------------
// Board / Senior-Management demo cohort login (2026-06-16) — shared
// "visible-but-dead" write suppression.
//
// When the session is a view-only demo cohort session, owner-data write verbs
// are short-circuited HERE (no network call) and a gentle notice is shown, so
// write controls "fall dead" cleanly instead of surfacing the server's 403.
// This is a SHARED layer — one place, not button-by-button. The SERVER block
// (requireNotStakeholder) remains the real guard; this is cosmetic only.
//
// Scope: only identity-service data writes are suppressed. auth-service session
// ops (logout / refresh / stakeholder switch) pass through. The read-side
// /download-audit POST is exempt — the actual download is a GET; that POST only
// records intent and must keep working (Ken authorised downloads).
let _demoViewOnly = false;
export function setDemoViewOnly(on) { _demoViewOnly = !!on; }

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const DEMO_VIEW_ONLY_MESSAGE = 'View-only demo — changes are not saved.';

function isSuppressedWrite(method, service, path) {
  if (!_demoViewOnly) return false;
  if (!WRITE_METHODS.has(method)) return false;
  if (service !== 'identity-service') return false;
  if (path.includes('/download-audit')) return false; // read-side audit, allowed
  return true;
}

function notifyDemoViewOnly() {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new CustomEvent('alloggio:demo-view-only', {
        detail: { message: DEMO_VIEW_ONLY_MESSAGE }
      }));
    } catch { /* no-op */ }
  }
}

// CSE read-only impersonation (Sean 2026-07-21) — graceful catch-all. When a
// write returns the server's 403 `read_only_impersonation`, surface the same
// gentle transient toast the view-only demo layer uses (DemoViewOnlyToast
// listens for this event) with the server's friendly message, so any write
// control we haven't individually disabled fails cleanly instead of throwing a
// scary generic error. The 403 itself is still returned to the caller — the
// backend block is the real guarantee; this is cosmetic.
const READ_ONLY_IMPERSONATION_MESSAGE =
  "View-only: you can view this owner's portal but cannot make changes.";
function notifyReadOnlyImpersonation(message) {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(new CustomEvent('alloggio:demo-view-only', {
        detail: { message: message || READ_ONLY_IMPERSONATION_MESSAGE }
      }));
    } catch { /* no-op */ }
  }
}

// Soft result for a suppressed write: a non-2xx status so caller success paths
// skip (no phantom state), carrying a gentle message many handlers surface
// inline. The global toast is the belt-and-braces notice.
function demoViewOnlyResult() {
  notifyDemoViewOnly();
  return {
    status: 423,
    data: { demo_view_only: true, error: 'demo_view_only', message: DEMO_VIEW_ONLY_MESSAGE }
  };
}

async function request(method, service, path, body) {
  if (isSuppressedWrite(method, service, path)) {
    return demoViewOnlyResult();
  }

  const options = {
    method,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' }
  };
  if (body) options.body = JSON.stringify(body);

  const response = await fetch(`/server/${service}${path}`, options);
  const data = await response.json().catch(() => ({}));
  // Graceful catch-all for CSE view-only impersonation writes (see helper).
  if (response.status === 403 && data && data.code === 'read_only_impersonation') {
    notifyReadOnlyImpersonation(typeof data.error === 'string' ? data.error : undefined);
  }
  return { status: response.status, data };
}

// Brand Email Status dashboard (Internal Tools PRD Phase 1).
//
// CHANGED 2026-08-18: this dashboard USED to be independent of the portal auth
// layer — a static passcode minted a bearer token and no session was involved,
// which meant anyone with the passcode could open it from any browser. It now
// requires BOTH a live staff session cookie (requireAuth) AND the caller being
// on BRAND_EMAIL_DASH_ALLOWED_EMAILS. So these calls send the session cookie
// *and* the dashboard token; the custom X-Dash-Token header remains because
// Catalyst intercepts `Authorization: Bearer …` on /server/ calls.
const BRAND_DASH_BASE = '/server/identity-service/identity/brand-email-dashboard';
async function brandDashFetch(method, path, { token, body } = {}) {
  const headers = {};
  if (body) headers['Content-Type'] = 'application/json';
  // Use a CUSTOM header, NOT Authorization: the Catalyst platform intercepts any
  // `Authorization: Bearer …` on /server/ calls and validates it as a Catalyst
  // OAuth token — our dashboard JWT isn't one, so it 401s ("invalid oauth token")
  // before reaching our code. X-Dash-Token passes straight through to the app.
  if (token) headers['X-Dash-Token'] = token;
  const res = await fetch(`${BRAND_DASH_BASE}${path}`, {
    method,
    // The staff session cookie is REQUIRED now (2026-08-18) — these routes sit
    // behind requireAuth. Same-origin would send it by default, but state it
    // explicitly: the gate depends on it, so it must not rest on a default.
    credentials: 'include',
    headers,
    ...(body ? { body: JSON.stringify(body) } : {})
  });
  const data = await res.json().catch(() => ({}));
  return { status: res.status, data };
}

export const brandEmailDashApi = {
  // Exchange the LIVE staff portal session for the dashboard token. Replaced
  // the static-passcode login on 2026-08-18 — the backend grants a token only
  // to a signed-in staff member on BRAND_EMAIL_DASH_ALLOWED_EMAILS, and 403s
  // everyone else. The session cookie rides along automatically (same-origin).
  session: () => brandDashFetch('POST', '/session'),
  getStatus: (token) => brandDashFetch('GET', '/status', { token }),
  // Dedup Sweep (tracker E42) — submit a per-brand bookings_duplicate_cleanup
  // run instead of editing BOOKINGS_DEDUP_BRAND on Production between brands.
  // One-shot jobs, dry-run by default (operator decision 2026-09-06).
  listDedupSweepBrands: (token) => brandDashFetch('GET', '/dedup-sweep/brands', { token }),
  // dryRun is passed EXPLICITLY on every call. Production runs with
  // BOOKINGS_DEDUP_DRY_RUN=false, so an omitted flag would delete rows on the
  // first click — the job param always wins over that env value.
  submitDedupSweep: (token, brand, dryRun) =>
    brandDashFetch('POST', '/dedup-sweep/submit', { token, body: { brand, dry_run: dryRun !== false } }),
  getDedupSweepJob: (token, jobId) =>
    brandDashFetch('GET', `/dedup-sweep/job?job_id=${encodeURIComponent(jobId)}`, { token }),
  test: (token, { brand, property_manager_email, to }) =>
    brandDashFetch('POST', '/test', { token, body: { brand, property_manager_email, to } }),
  // Invite Tester — send the REAL owner invite email with the REAL branded URL
  // (NZ → owners.alloggio.co.nz, AU → owners.alloggio.com.au) for a brand, so the
  // operator can verify the go-live link. Proxies to auth-service test-preview;
  // creates NO records.
  testInvite: (token, { brand, to }) =>
    brandDashFetch('POST', '/test-invite', { token, body: { brand, to } }),
  // Per-property "Owner Income shows only after arrival" (income_on_arrival)
  // admin — same passcode/token gate as the rest of this dashboard.
  listIncomeOnArrival: (token) => brandDashFetch('GET', '/income-on-arrival', { token }),
  setIncomeOnArrival: (token, propertyId, enabled) =>
    brandDashFetch('POST', '/income-on-arrival/set', { token, body: { property_id: propertyId, enabled: !!enabled } }),
  // Brand-Sender CRUD (Brand Senders tab). BrandSenders is the operator table
  // brandResolver keys off to pick a brand's invite/notice pm@ sender.
  listBrandSenders: (token) => brandDashFetch('GET', '/brand-senders', { token }),
  createBrandSender: (token, { brand_name, pm_email, owner_care_line }) =>
    brandDashFetch('POST', '/brand-sender', { token, body: { brand_name, pm_email, owner_care_line } }),
  // All value fields optional — pass only what changes (pm_email / owner_care_line /
  // live / zoho_setup_done / catalyst_setup_done). The two setup flags are pure
  // operator self-tracking — they do NOT gate invites, comms, or anything else.
  // NB: this destructures an explicit field list, so ANY new BrandSenders field
  // must be added in BOTH places below or it is silently dropped and the PUT
  // comes back "Nothing to update." (cost us a debug round on
  // owner_clean_choice_enabled, 2026-08-21).
  updateBrandSender: (token, { rowid, pm_email, owner_care_line, live, zoho_setup_done, catalyst_setup_done, region, owner_clean_choice_enabled, hide_properties }) =>
    brandDashFetch('PUT', '/brand-sender', { token, body: { rowid, pm_email, owner_care_line, live, zoho_setup_done, catalyst_setup_done, region, owner_clean_choice_enabled, hide_properties } }),
  // Backfill Brands CRUD (Backfill Brands tab). BackfillBrands is the operator
  // table the guesty/homhero bookings-backfill crons read to decide which brands
  // to sweep. EDIT-ONLY — the table is pre-seeded, so no create/delete. All value
  // fields optional on update; the backend validates dates/source/uniqueness.
  listBackfillBrands: (token) => brandDashFetch('GET', '/backfill-brands', { token }),
  updateBackfillBrand: (token, { rowid, source, office, brand_short_code, start_date, end_date, allow_backfill }) =>
    brandDashFetch('PUT', '/backfill-brand', { token, body: { rowid, source, office, brand_short_code, start_date, end_date, allow_backfill } }),
  // Owner Access — reset & re-invite (Owner Access tab). Look up an owner's
  // login state by email, then clear their credential + lockout and fire a
  // fresh invite in one call.
  ownerLookup: (token, email) =>
    brandDashFetch('GET', `/owner-lookup?email=${encodeURIComponent(email)}`, { token }),
  ownerReset: (token, { owner_contact_id, send_invite }) =>
    brandDashFetch('POST', '/owner-reset', { token, body: { owner_contact_id, send_invite } }),
  // Unlock User (Unlock User tab) — clears ONLY the lockout (status → active,
  // failed_login_attempts → 0, locked_until → null). Never touches password/MFA
  // and never sends an invite. Deliberately separate from ownerReset.
  ownerUnlock: (token, { email, owner_contact_id }) =>
    brandDashFetch('POST', '/owner-unlock', { token, body: { email, owner_contact_id } }),
  // Invited Users tab — per-brand property invite grid. invitedUsers lists a
  // brand's properties with owner + invite status; sendInvite fires a fresh
  // invite for one property's owner.
  invitedUsers: (token, brand) =>
    brandDashFetch('GET', `/invited-users?brand=${encodeURIComponent(brand)}`, { token }),
  sendInvite: (token, { owner_contact_id }) =>
    brandDashFetch('POST', '/send-invite', { token, body: { owner_contact_id } }),
  // Duplicate-owner blacklist management (BlacklistedDuplicateOwners table). The
  // crm_sync_cron_all enforces the table; the invite paths respect it. These calls
  // are the operator's front door to it: list / add / remove. All fail-soft on a
  // missing table (list → [], add/remove → { ok:false, table_missing:true }).
  listBlacklist: (token) => brandDashFetch('GET', '/blacklist', { token }),
  // Blacklisting now SUSPENDS the owner's portal login as well, so the add is
  // guarded server-side and can come back 409:
  //   multiple_active_rows — bug E34, one id with 2+ active owner records.
  //                          REFUSED outright; no flag lifts it.
  //   has_live_login       — this record holds the live login. A WARNING:
  //                          re-send with confirm_live_login:true to proceed.
  addBlacklist: (token, { owner_contact_id, reason, confirm_live_login }) =>
    brandDashFetch('POST', '/blacklist', {
      token,
      body: { owner_contact_id, reason, ...(confirm_live_login ? { confirm_live_login: true } : {}) }
    }),
  // Removing lifts the block but NEVER auto-restores a suspended login — they
  // may have been suspended first for an unrelated reason. The response reports
  // `was_suspended`; pass restore_login:true to un-suspend in the same action.
  removeBlacklist: (token, { owner_contact_id, restore_login }) =>
    brandDashFetch('DELETE', '/blacklist', {
      token,
      body: { owner_contact_id, ...(restore_login ? { restore_login: true } : {}) }
    }),
  // DELETE THE RECORD ITSELF — irreversible, and a different thing from
  // removeBlacklist above (which only lifts the block). The server enforces
  // every guard: blacklisted, no portal user, no active entity link, no active
  // property, and a surviving twin on the same email. `confirm` must be the
  // literal string DELETE; the server refuses without it, so this cannot be
  // triggered by a stray click. A refusal comes back 409 with a `reason`.
  purgeBlacklistedOwner: (token, { owner_contact_id }) =>
    brandDashFetch('POST', '/blacklist/purge', {
      token,
      body: { owner_contact_id, confirm: 'DELETE' }
    }),
  // Duplicate Owners tab — every email in a brand that maps to 2+ owner records,
  // with the per-record facts the blacklist decision needs. Read-only; the fix
  // itself goes through addBlacklist above, so there is ONE write path.
  duplicateOwners: (token, brand) =>
    brandDashFetch('GET', `/duplicate-owners?brand=${encodeURIComponent(brand)}`, { token }),
  // E34 cleanup — ONE CRM contact holding two OwnerContact rows. Deletes the
  // surplus row(s) and keeps the one furthest along on invitation_status. Only
  // for shape B; the server re-checks the id-count itself rather than trusting
  // the browser. Idempotent: a second click finds one row and reports success.
  removeDuplicateRow: (token, { owner_contact_id }) =>
    brandDashFetch('POST', '/duplicate-row/remove', { token, body: { owner_contact_id } }),
  // Owner Support tab — identity, login state, duplicate twins, properties,
  // brand and brand sender for one email, in a single call. The ACTIONS on that
  // tab reuse ownerReset / ownerUnlock / the impersonation route, so there is no
  // second write path for any of them.
  ownerOverview: (token, email) =>
    brandDashFetch('GET', `/owner-overview?email=${encodeURIComponent(email)}`, { token }),
  // Persist the operator's per-property CRM-cleanup note (Properties.cleanup_note,
  // a portal-managed column that survives CRM syncs). Empty note clears it.
  updatePropertyNote: (token, { property_id, note }) =>
    brandDashFetch('PUT', '/property-note', { token, body: { property_id, note } }),
  // Image Coverage tab — per-brand list of properties + whether each has images
  // in the portal. The client builds the downloadable CSV from this response.
  // Paginated: the endpoint processes at most `limit` active properties per call
  // (WorkDrive reads are slow at brand scale). The client walks the slices
  // (offset += limit until has_more is false) and accumulates one CSV.
  imageCoverage: (token, brand, { offset = 0, limit } = {}) =>
    brandDashFetch(
      'GET',
      `/image-coverage?brand=${encodeURIComponent(brand)}&offset=${offset}` +
        (limit ? `&limit=${limit}` : ''),
      { token }
    ),
  // Statement Coverage tab — per-brand list of properties + whether each has
  // owner statements in the portal. The client builds the downloadable CSV from
  // this response. Statement folders are read live (not cached).
  // Batched: pass an offset to fetch the next slice. The response carries
  // next_offset (null when finished) — see statementCoverageAll below, which is
  // what callers should normally use.
  statementCoverage: (token, brand, offset = 0) =>
    brandDashFetch(
      'GET',
      `/statement-coverage?brand=${encodeURIComponent(brand)}&offset=${encodeURIComponent(offset)}`,
      { token }
    ),
  // MA Coverage tab — per-brand list of properties + whether each has a
  // Management Agreement linked in CRM (Properties.management_agreement_id).
  // Pure DataStore read (no WorkDrive). The client builds the CSV from this.
  maCoverage: (token, brand) =>
    brandDashFetch('GET', `/ma-coverage?brand=${encodeURIComponent(brand)}`, { token }),
  // Property Owners tab — look up a property (by exact property_id or a name/id
  // substring `q`) and list its currently-linked owners; then unlink ONLY a
  // stale (owner_entity_id ↔ property_id) EntityProperty link. Same passcode
  // gate. The unlink deactivates just that one link — nothing else changes.
  propertyOwners: (token, { property_id, q } = {}) =>
    brandDashFetch(
      'GET',
      property_id
        ? `/property-owners?property_id=${encodeURIComponent(property_id)}`
        : `/property-owners?q=${encodeURIComponent(q || '')}`,
      { token }
    ),
  unlinkOwner: (token, { property_id, owner_entity_id }) =>
    brandDashFetch('POST', '/unlink-owner', { token, body: { property_id, owner_entity_id } }),
  // Remove ONE PERSON from ONE OWNER ENTITY (the EntityContact grain). Access is
  // the cross product of EntityContact and EntityProperty — there is no
  // (person, property) row — so unlinkOwner above removes EVERY person on the
  // entity, and this one removes the person from EVERY property the entity
  // owns. The UI states which grain it is about to use.
  unlinkOwnerContact: (token, { owner_entity_id, owner_contact_id }) =>
    brandDashFetch('POST', '/unlink-owner-contact', { token, body: { owner_entity_id, owner_contact_id } }),
  // Property Owners tab — "Browse by brand" mode. Lists EVERY property in a brand
  // with ALL its currently-linked owners underneath, so stale/duplicate owner
  // links (an old owner still linked alongside the new one) are obvious at a
  // glance. Read-only; each owner row reuses the same unlinkOwner call above.
  brandPropertyOwners: (token, brand) =>
    brandDashFetch('GET', `/brand-property-owners?brand=${encodeURIComponent(brand)}`, { token }),
  // CRM Owner Checker tab — read-only, straight from CRM. Per property: the
  // Area's Owner vs the contacts on the Area's Account, so a brand can be
  // checked before it is invited (an ownership change leaves the old owner
  // linked and the new one invisible).
  crmOwnerCheck: (token, brand) =>
    brandDashFetch('GET', `/crm-owner-check?brand=${encodeURIComponent(brand)}`, { token }),
  // Image Importer tab — per-property, on-demand cache warming to the Stratus
  // `property-images` bucket. imageImportList is the cheap ZCQL-only property
  // roster for a brand (no WorkDrive, no bucket probing); importImages warms ONE
  // property's images (thumb + medium) into the bucket, operator-paced. The POST
  // is throttled + 429-backoff server-side, so a `stopped_on_rate_limit` result
  // just means "click again to resume" (already-warmed images are skipped).
  imageImportList: (token, brand) =>
    brandDashFetch('GET', `/image-import-list?brand=${encodeURIComponent(brand)}`, { token }),
  importImages: (token, propertyId) =>
    brandDashFetch('POST', '/import-images', { token, body: { property_id: propertyId } }),
  // Image ZIP tab — download ONE property's thumb+medium renditions, packed into
  // a bucket-structured ZIP (…/thumb/<fileId>, …/medium/<fileId>) for manual
  // upload to the Stratus console. Binary-aware: on success the response is
  // application/zip (returned as a Blob + the X-Zip-* cursor headers); on
  // no-folder / empty / rate-limit / error the server returns JSON instead. A
  // big property comes back in resumable parts — pass `after` = the previous
  // part's next_index to continue.
  imageZip: async (token, propertyId, { after = 0 } = {}) => {
    const headers = {};
    if (token) headers['X-Dash-Token'] = token;
    const res = await fetch(
      `${BRAND_DASH_BASE}/image-zip?property_id=${encodeURIComponent(propertyId)}&after=${after}`,
      { method: 'GET', headers }
    );
    const ct = (res.headers.get('content-type') || '').toLowerCase();
    if (res.ok && ct.includes('application/zip')) {
      const blob = await res.blob();
      return {
        status: res.status,
        zip: true,
        blob,
        meta: {
          images_total: Number(res.headers.get('X-Zip-Images-Total')) || 0,
          images_included: Number(res.headers.get('X-Zip-Images-Included')) || 0,
          next_index: Number(res.headers.get('X-Zip-Next-Index')) || 0,
          has_more: res.headers.get('X-Zip-Has-More') === 'true',
          stopped_on_rate_limit: res.headers.get('X-Zip-Stopped-Rate-Limit') === 'true'
        }
      };
    }
    const data = await res.json().catch(() => ({}));
    return { status: res.status, zip: false, data };
  }
};

export const authApi = {
  login: (email, password) => request('POST', 'auth-service', '/auth/login', { email, password }),
  me: () => request('GET', 'auth-service', '/auth/me'),
  logout: () => request('POST', 'auth-service', '/auth/logout'),
  // Sliding idle-session renewal (Foundation PRD v0.3 US-F01). Re-issues a
  // fresh session cookie when the current one is still valid; 401 if expired.
  refresh: () => request('POST', 'auth-service', '/auth/refresh')
};

// Board / Senior-Management demo cohort login (2026-06-16). The shared demo
// login lists its cohort (getCohort) and switches the viewed owner
// (switchOwner). switchOwner is an auth-service session re-issue, so it is NOT
// suppressed by the view-only layer above (which only blocks identity-service
// data writes).
export const stakeholderCohortApi = {
  getCohort: () => request('GET', 'auth-service', '/auth/stakeholder/cohort'),
  switchOwner: (ownerContactId) =>
    request('POST', 'auth-service', '/auth/stakeholder/view', { owner_contact_id: ownerContactId })
};

// US-stakeholder-identify (2026-05-04) — IdentifyMe modal posts the human's
// name + self-described role here once per shared-login session. Backend
// writes a 'stakeholder_session_identified' event to AuditLog.
export async function postStakeholderIdentify({ name, role }) {
  const res = await fetch('/server/auth-service/auth/stakeholder/identify', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ name, role })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errMsg = (data && (data.error?.message || data.error)) || `Identify failed (${res.status})`;
    throw new Error(typeof errMsg === 'string' ? errMsg : `Identify failed (${res.status})`);
  }
  return data;
}

export const identityApi = {
  getProperties: () => request('GET', 'identity-service', '/identity/properties'),
  getProperty: (propertyId) =>
    request('GET', 'identity-service', `/identity/properties/${encodeURIComponent(propertyId)}`),
  // UAT-D-007 Phase 2 — staff-only "set/clear property lead image". Staff reach
  // the owner property-detail page via impersonation; the backend gates on the
  // real staff principal. Owners never see the control that calls this.
  setPropertyHeroImage: (propertyId, fileId) =>
    request('POST', 'identity-service', `/identity/properties/${encodeURIComponent(propertyId)}/hero-image`, { fileId }),
  clearPropertyHeroImage: (propertyId) =>
    request('DELETE', 'identity-service', `/identity/properties/${encodeURIComponent(propertyId)}/hero-image`),
  getActionRequired: () => request('GET', 'identity-service', '/identity/action-required'),
  getCommunications: () => request('GET', 'identity-service', '/identity/communications'),
  getDocuments: (selectedPropertyId) => {
    const qs = selectedPropertyId
      ? `?selected_property_id=${encodeURIComponent(selectedPropertyId)}`
      : '';
    return request('GET', 'identity-service', `/identity/documents${qs}`);
  },
  getDocumentPreviewUrl: (id) =>
    request('GET', 'identity-service', `/identity/documents/${encodeURIComponent(id)}/preview-url`),
  getDocumentDownloadUrl: (id) =>
    request('GET', 'identity-service', `/identity/documents/${encodeURIComponent(id)}/download-url`),
  postDocumentDownloadAudit: (id, propertyId) =>
    request('POST', 'identity-service', `/identity/documents/${encodeURIComponent(id)}/download-audit`, { property_id: propertyId }),
  getFinancials: (selectedPropertyId, fyStartDate) => {
    // UAT-D-004 — pass the property-dropdown filter so server-side cards rescope
    // to the single property. Omit for portfolio totals.
    // UAT #11 (Sean 2026-06-29) — pass the FY filter (fy_start_date) so the
    // owner-income cards rescope to the selected financial year. Omit for the
    // current FY (server default).
    const qs = new URLSearchParams();
    if (selectedPropertyId) qs.set('selected_property_id', selectedPropertyId);
    if (fyStartDate) qs.set('fy_start_date', fyStartDate);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request('GET', 'identity-service', `/identity/financials${suffix}`);
  },
  getFinancialsStatements: (selectedPropertyId) => {
    const qs = selectedPropertyId
      ? `?selected_property_id=${encodeURIComponent(selectedPropertyId)}`
      : '';
    return request('GET', 'identity-service', `/identity/financials/statements${qs}`);
  },
  getFinancialsStatementDownloadUrl: (statementId) =>
    request('GET', 'identity-service', `/identity/financials/statements/${encodeURIComponent(statementId)}/download-url`),
  getFinancialsStatementPreviewUrl: (statementId) =>
    request('GET', 'identity-service', `/identity/financials/statements/${encodeURIComponent(statementId)}/preview-url`),
  getInternalToolTiles: () => request('GET', 'identity-service', '/identity/internal-tools/tiles'),
  getSyncDiagnostics: () => request('GET', 'identity-service', '/identity/internal-tools/sync-diagnostics'),
  postSyncDiagnosticsReplay: (cronName) =>
    request('POST', 'identity-service', `/identity/internal-tools/sync-diagnostics/${encodeURIComponent(cronName)}/replay`),
  getOwnerLookup: ({ q = '', brand = '', limit = 50, offset = 0 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (brand) params.set('brand', brand);
    if (limit) params.set('limit', String(limit));
    if (offset) params.set('offset', String(offset));
    const qs = params.toString();
    return request('GET', 'identity-service', `/identity/internal-tools/owner-lookup${qs ? `?${qs}` : ''}`);
  },
  postOwnerLookupActAs: (ownerContactId) =>
    request('POST', 'identity-service', `/identity/internal-tools/owner-lookup/${encodeURIComponent(ownerContactId)}/act-as`),
  // Reset & resend from Owner Lookup — staff-authed twin of the Brand Email
  // dashboard reset (Danny, 2026-08-27).
  postOwnerLookupReset: (ownerContactId) =>
    request('POST', 'identity-service', `/identity/internal-tools/owner-lookup/${encodeURIComponent(ownerContactId)}/reset`),
  // Staff-initiated accountant access from Owner Lookup (Karen Cooper,
  // 2026-09-09 — owners struggle to do this themselves, so the team does it
  // when an owner emails asking). Staff twin of the owner-side
  // /identity/accountant routes; takes owner_contact_id explicitly.
  getStaffAccountantList: (ownerContactId) =>
    request('GET', 'identity-service', `/identity/accountant/staff/list?owner_contact_id=${encodeURIComponent(ownerContactId)}`),
  postStaffAccountantInvite: (ownerContactId, email, inviteeName) =>
    request('POST', 'identity-service', '/identity/accountant/staff/invite', {
      owner_contact_id: ownerContactId,
      email,
      invitee_name: inviteeName
    }),
  postStaffAccountantRevoke: (ownerContactId, invitationId) =>
    request('POST', 'identity-service', '/identity/accountant/staff/revoke', {
      owner_contact_id: ownerContactId,
      invitation_id: invitationId
    }),
  // Owner-side accountant delegation (Settings). The staff twins above take an
  // owner_contact_id explicitly; these resolve the owner from the session, so
  // an owner can only ever manage their OWN accountants.
  getAccountants: () =>
    request('GET', 'identity-service', '/identity/accountant/list'),
  postAccountantInvite: (inviteeName, email) =>
    request('POST', 'identity-service', '/identity/accountant/invite', {
      invitee_name: inviteeName,
      email
    }),
  postAccountantRevoke: (invitationId) =>
    request('POST', 'identity-service', '/identity/accountant/revoke', {
      invitation_id: invitationId
    }),
  postOwnerLookupActAsExit: () =>
    request('POST', 'identity-service', '/identity/internal-tools/owner-lookup/act-as/exit'),
  // Impersonate Owner tile (2026-05-15) — thin wrapper around the same
  // act-as machinery, tech_admin-only, email-input entry.
  postImpersonationStart: (owner_email) =>
    request('POST', 'identity-service', '/identity/internal-tools/impersonation/start', { owner_email }),
  postImpersonationEnd: () =>
    request('POST', 'identity-service', '/identity/internal-tools/impersonation/end'),
  getImpersonationStatus: () =>
    request('GET', 'identity-service', '/identity/internal-tools/impersonation/status'),
  getAuditLog: (params = {}) => {
    const qs = new URLSearchParams();
    for (const k of ['event_type', 'actor', 'target', 'from', 'to', 'q', 'sort']) {
      if (params[k]) qs.set(k, String(params[k]));
    }
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/internal-tools/audit-log${qstr ? `?${qstr}` : ''}`);
  },
  // Build the full URL for the CSV export so the page can hand it to
  // <a href> / window.open. Same query params as getAuditLog (limit /
  // offset are ignored server-side).
  buildAuditLogExportUrl: (params = {}) => {
    const qs = new URLSearchParams();
    for (const k of ['event_type', 'actor', 'target', 'from', 'to', 'q', 'sort']) {
      if (params[k]) qs.set(k, String(params[k]));
    }
    const qstr = qs.toString();
    return `/server/identity-service/identity/internal-tools/audit-log/export.csv${qstr ? `?${qstr}` : ''}`;
  },
  // CR-006 — What's New / changelog admin-approval workflow.
  // Owner-facing feed: ONLY entries an admin has approved for owner visibility.
  getWhatsNewReleases: () =>
    request('GET', 'identity-service', '/identity/changelog/whats-new'),
  // Admin queue (tech_admin + ops_leadership).
  getChangelogEntries: () =>
    request('GET', 'identity-service', '/identity/internal-tools/changelog'),
  // 3-level visibility (Sean 2026-06-10): admin_only | internal_staff | owner_facing.
  postChangelogVisibility: (entryId, visibility) =>
    request('POST', 'identity-service', `/identity/internal-tools/changelog/${encodeURIComponent(entryId)}/visibility`, { visibility }),
  putChangelogOwnerCopy: (entryId, ownerCopy) =>
    request('PUT', 'identity-service', `/identity/internal-tools/changelog/${encodeURIComponent(entryId)}/owner-copy`, { owner_copy: ownerCopy }),
  postChangelogSync: () =>
    request('POST', 'identity-service', '/identity/internal-tools/changelog/sync'),
  postChangelogMigrateVisibility: () =>
    request('POST', 'identity-service', '/identity/internal-tools/changelog/migrate-visibility'),
  getBookingsCalendar: ({ propertyIds, from, to } = {}) => {
    const qs = new URLSearchParams();
    if (Array.isArray(propertyIds) && propertyIds.length > 0) {
      qs.set('property_ids', propertyIds.join(','));
    }
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/bookings/calendar${qstr ? `?${qstr}` : ''}`);
  },
  // guest_* fields are sent for owner-guest (friends & family) bookings only —
  // the guest is a third party there. Every other block type omits them and the
  // server uses the owner's own CRM details, unchanged.
  postBookingsBlock: ({
    property_id, block_type, start_date, end_date, notes, guests_count, cleaning_option,
    guest_first_name, guest_last_name, guest_email, guest_phone
  }) =>
    request('POST', 'identity-service', '/identity/bookings/calendar/blocks', {
      property_id, block_type, start_date, end_date, notes, guests_count, cleaning_option,
      guest_first_name, guest_last_name, guest_email, guest_phone
    }),
  patchBookingsBlock: (blockId, { start_date, end_date, notes }) =>
    request('PATCH', 'identity-service', `/identity/bookings/calendar/blocks/${encodeURIComponent(blockId)}`, {
      start_date, end_date, notes
    }),
  deleteBookingsBlock: (blockId) =>
    request('DELETE', 'identity-service', `/identity/bookings/calendar/blocks/${encodeURIComponent(blockId)}`),
  getMaintenance: () => request('GET', 'identity-service', '/identity/maintenance'),
  getMaintenanceTickets: ({ propertyId, status, limit, offset } = {}) => {
    const qs = new URLSearchParams();
    if (propertyId) qs.set('property_id', propertyId);
    if (status) qs.set('status', status);
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/maintenance/tickets${qstr ? `?${qstr}` : ''}`);
  },
  getMaintenanceTicket: (ticketId) =>
    request('GET', 'identity-service', `/identity/maintenance/tickets/${encodeURIComponent(ticketId)}`),
  // Maintenance & Messaging iter-1 (Phase 1) — submit is multipart so
  // we can attach photos. Replies remain JSON (text-only Phase 1).
  postMaintenanceTicket: async ({ property_id, title, description, urgency, request_type, photo_files }) => {
    // Shared view-only-demo suppression (this write bypasses request()).
    if (_demoViewOnly) return demoViewOnlyResult();
    const fd = new FormData();
    fd.append('property_id', property_id || '');
    fd.append('title', title || '');
    fd.append('description', description || '');
    fd.append('urgency', urgency || '');
    fd.append('request_type', request_type || '');
    if (Array.isArray(photo_files)) {
      for (const f of photo_files) fd.append('photos', f, f.name);
    }
    const res = await fetch('/server/identity-service/identity/maintenance/tickets', {
      method: 'POST',
      credentials: 'include',
      body: fd
    });
    const data = await res.json().catch(() => ({}));
    return { status: res.status, data };
  },
  postMaintenanceTicketReply: (ticketId, { body }) =>
    request('POST', 'identity-service', `/identity/maintenance/tickets/${encodeURIComponent(ticketId)}/replies`, { body }),
  getPerformance: () => request('GET', 'identity-service', '/identity/performance'),
  // Performance & Reviews PRD iter-1 (Phase 1, Guesty-source-only)
  getPerformanceReviews: ({ propertyId, since, until, limit } = {}) => {
    const qs = new URLSearchParams();
    if (propertyId) qs.set('propertyId', propertyId);
    if (since) qs.set('since', since);
    if (until) qs.set('until', until);
    if (limit) qs.set('limit', String(limit));
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/performance/reviews${qstr ? `?${qstr}` : ''}`);
  },
  getPerformanceStats: ({ propertyId } = {}) => {
    const qs = new URLSearchParams();
    if (propertyId) qs.set('propertyId', propertyId);
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/performance/stats${qstr ? `?${qstr}` : ''}`);
  },
  getPerformanceTimeseries: ({ metric = 'review_score', grain = 'daily', window = 30, propertyId } = {}) => {
    const qs = new URLSearchParams();
    qs.set('metric', metric);
    qs.set('grain', grain);
    qs.set('window', String(window));
    if (propertyId) qs.set('propertyId', propertyId);
    return request('GET', 'identity-service', `/identity/performance/timeseries?${qs.toString()}`);
  },
  getPerformanceBenchmarks: ({ propertyId } = {}) => {
    const qs = new URLSearchParams();
    if (propertyId) qs.set('propertyId', propertyId);
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/performance/benchmarks${qstr ? `?${qstr}` : ''}`);
  },
  getSettings: () => request('GET', 'identity-service', '/identity/settings'),
  // Settings & Bank Account PRD Chip B — owner-facing bank-change submit flow.
  // GET returns the owner's currently in-flight request (or null).
  getBankChange: () => request('GET', 'identity-service', '/identity/settings/bank-change'),
  // POST submits a new request. Sends multipart/form-data because the
  // government-ID document is a real binary upload, so we go around the
  // JSON `request()` helper and call fetch directly.
  // Settings & Bank Account PRD Chip E — owner-facing notification preferences.
  getNotificationPreferences: () =>
    request('GET', 'identity-service', '/identity/settings/notification-preferences'),
  putNotificationPreferences: (updates) =>
    request('PUT', 'identity-service', '/identity/settings/notification-preferences', { updates }),
  // Settings & Bank Account PRD Chip F — profile editability + locked-field
  // request-edit ticket flow.
  getProfile: () => request('GET', 'identity-service', '/identity/settings/profile'),
  putProfileEditable: ({ display_name, language }) =>
    request('PUT', 'identity-service', '/identity/settings/profile/editable', {
      display_name, language
    }),
  postPasswordChange: ({ current_password, new_password }) =>
    request('POST', 'identity-service', '/identity/settings/password', {
      current_password, new_password
    }),
  postLockedFieldRequestEdit: ({ field, proposed_value, justification }) =>
    request('POST', 'identity-service', '/identity/settings/profile/locked/request-edit', {
      field, proposed_value, justification
    }),
  postBankChange: async ({ proposed_bsb, proposed_account_number, reason, gov_id_document }) => {
    // Shared view-only-demo suppression (this write bypasses request()).
    if (_demoViewOnly) return demoViewOnlyResult();
    const form = new FormData();
    form.append('proposed_bsb', proposed_bsb || '');
    form.append('proposed_account_number', proposed_account_number || '');
    form.append('reason', reason || '');
    if (gov_id_document) {
      form.append('gov_id_document', gov_id_document, gov_id_document.name);
    }
    const response = await fetch('/server/identity-service/identity/settings/bank-change', {
      method: 'POST',
      credentials: 'include',
      body: form
    });
    const data = await response.json().catch(() => ({}));
    return { status: response.status, data };
  },
  // US-F18 — Admin Invitation Queue (Staff Dashboard)
  getAdminQueue: ({ status = 'open', action_type, q, limit = 50, offset = 0 } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (action_type) qs.set('action_type', action_type);
    if (q) qs.set('q', q);
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/staff/admin-queue${qstr ? `?${qstr}` : ''}`);
  },
  postAdminQueueResend: (id, { reason }) =>
    request('POST', 'identity-service', `/identity/staff/admin-queue/${encodeURIComponent(id)}/resend`, { reason }),
  postAdminQueueHold: (id, { reason }) =>
    request('POST', 'identity-service', `/identity/staff/admin-queue/${encodeURIComponent(id)}/hold`, { reason }),
  postAdminQueueLiftHold: (id, { reason } = {}) =>
    request('POST', 'identity-service', `/identity/staff/admin-queue/${encodeURIComponent(id)}/lift-hold`, { reason }),
  postAdminQueueDismiss: (id, { reason }) =>
    request('POST', 'identity-service', `/identity/staff/admin-queue/${encodeURIComponent(id)}/dismiss`, { reason }),

  // Settings & Bank Account PRD, Chip D — staff review queue for bank-account
  // change requests. RBAC: tech_admin + ops_leadership + trust_accountant
  // (enforced server-side; tile is hidden client-side for other roles).
  getBankChangeQueue: ({ status = 'open', page = 0, pageSize = 50 } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (Number.isFinite(page)) qs.set('page', String(page));
    if (Number.isFinite(pageSize)) qs.set('pageSize', String(pageSize));
    const qstr = qs.toString();
    return request('GET', 'identity-service', `/identity/internal-tools/bank-change-queue${qstr ? `?${qstr}` : ''}`);
  },
  getBankChangeQueueCount: () =>
    request('GET', 'identity-service', '/identity/internal-tools/bank-change-queue/count'),
  getBankChangeRequest: (id) =>
    request('GET', 'identity-service', `/identity/internal-tools/bank-change-queue/${encodeURIComponent(id)}`),
  postBankChangeConfirmIdentity: (id, { notes } = {}) =>
    request('POST', 'identity-service', `/identity/internal-tools/bank-change-queue/${encodeURIComponent(id)}/confirm-identity`, { notes }),
  postBankChangeApprove: (id, { notes } = {}) =>
    request('POST', 'identity-service', `/identity/internal-tools/bank-change-queue/${encodeURIComponent(id)}/approve`, { notes }),
  postBankChangeReject: (id, { reason }) =>
    request('POST', 'identity-service', `/identity/internal-tools/bank-change-queue/${encodeURIComponent(id)}/reject`, { reason }),

  // Communication Console — sub-chip A (compose surface + template library).
  // Send wiring lands in sub-chip C; these three drive the read + preview path.
  getCommunicationTemplates: () =>
    request('GET', 'identity-service', '/identity/internal-tools/communication/templates'),
  postCommunicationAudiencePreview: (filters) =>
    request('POST', 'identity-service', '/identity/internal-tools/communication/audience-preview', { filters }),
  getCommunicationTradingNames: () =>
    request('GET', 'identity-service', '/identity/internal-tools/communication/trading-names'),
  postCommunicationComposePreview: ({ template_id, subject, body, merge_fields_sample } = {}) =>
    request('POST', 'identity-service', '/identity/internal-tools/communication/compose-preview', {
      template_id,
      subject,
      body,
      merge_fields_sample
    }),

  // Communication Console — sub-chip B (admin panel + settings backing store).
  // Narrower RBAC than the compose surface: tech_admin + ops_leadership only.
  getCommunicationSettings: () =>
    request('GET', 'identity-service', '/identity/internal-tools/communication/settings'),
  putCommunicationSettings: (patch) =>
    request('PUT', 'identity-service', '/identity/internal-tools/communication/settings', patch),

  // Communication Console — sub-chip C (send + approval queue).
  postCommunicationSend: ({ template_id, subject, body, audience_filters } = {}) =>
    request('POST', 'identity-service', '/identity/internal-tools/communication/send', {
      template_id,
      subject,
      body,
      audience_filters
    }),
  getCommunicationQueue: ({ status = 'pending', page = 0, pageSize = 50 } = {}) =>
    request('GET', 'identity-service',
      `/identity/internal-tools/communication/queue?status=${encodeURIComponent(status)}&page=${page}&pageSize=${pageSize}`),
  getCommunicationQueueDetail: (broadcastId) =>
    request('GET', 'identity-service',
      `/identity/internal-tools/communication/queue/${encodeURIComponent(broadcastId)}`),
  postCommunicationQueueApprove: (broadcastId) =>
    request('POST', 'identity-service',
      `/identity/internal-tools/communication/queue/${encodeURIComponent(broadcastId)}/approve`, {}),
  postCommunicationQueueReject: (broadcastId, reason) =>
    request('POST', 'identity-service',
      `/identity/internal-tools/communication/queue/${encodeURIComponent(broadcastId)}/reject`, { reason }),
  postCommunicationQueueCancel: (broadcastId) =>
    request('POST', 'identity-service',
      `/identity/internal-tools/communication/queue/${encodeURIComponent(broadcastId)}/cancel`, {})
};

// ---------------------------------------------------------------------------
// Financials PRD iter-2 (A11.3 + A11.4, Sean 2026-05-08).
//
// FYTD-by-country revenue + aggregation-toggle options. The revenue
// numbers themselves remain Preview (A11.1 / A11.2 parked) — what these
// helpers actually deliver is the FY window infrastructure + per-property
// toggle wiring.
//
// Placed at the bottom of the file per the chip brief to keep diff
// non-overlapping with the parallel Communication Console sub-chip C.
// ---------------------------------------------------------------------------
export function getFinancialsFYTDRevenue({ propertyId, aggregation = 'all' } = {}) {
  const qs = new URLSearchParams();
  if (aggregation) qs.set('aggregation', aggregation);
  if (propertyId) qs.set('property_id', propertyId);
  const qstr = qs.toString();
  return request('GET', 'identity-service', `/identity/financials/fytd-revenue${qstr ? `?${qstr}` : ''}`);
}

export function getFinancialsAggregationOptions() {
  return request('GET', 'identity-service', '/identity/financials/aggregation-options');
}

// Also expose on identityApi for parity with the existing call sites that
// consume `identityApi.getX(...)` rather than named imports.
identityApi.getFinancialsFYTDRevenue = getFinancialsFYTDRevenue;
identityApi.getFinancialsAggregationOptions = getFinancialsAggregationOptions;

// Staff Dashboard "Invite Owner" panel — transitional until the Internal
// Tools PRD ships its full admin UI. Staff (any role) post to the same
// /auth/invite endpoint admins use; backend gate is requireStaffOrInternalService.
export async function postInviteOwner({ email, owner_contact_id }) {
  const res = await fetch('/server/auth-service/auth/invite', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, owner_contact_id, trigger: 'staff_manual' })
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `Invite failed (${res.status})`);
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Calendar & Bookings PRD iter-1 Phase 1a (2026-05-13) — read-only helpers.
// Appended at bottom of file to avoid merge conflicts with parallel chips.
// The existing identityApi.getBookingsCalendar at line ~100 uses the
// older { from, to } shape; the new shape uses { start_date, end_date }
// to match the Phase 1a endpoint contract. Both call the same route —
// the route accepts either parameter set.
// ---------------------------------------------------------------------------

export const calendarApi = {
  // GET /identity/bookings/calendar
  // params: { propertyIds?: string[], startDate?: ISO, endDate?: ISO }
  getCalendarBlocks: ({ propertyIds, startDate, endDate } = {}) => {
    const qs = new URLSearchParams();
    if (Array.isArray(propertyIds) && propertyIds.length > 0) {
      qs.set('property_ids', propertyIds.join(','));
    }
    if (startDate) qs.set('start_date', startDate);
    if (endDate) qs.set('end_date', endDate);
    const qstr = qs.toString();
    return request(
      'GET',
      'identity-service',
      `/identity/bookings/calendar${qstr ? `?${qstr}` : ''}`
    );
  },

  // GET /identity/bookings/picker-options
  getCalendarPickerOptions: () =>
    request('GET', 'identity-service', '/identity/bookings/picker-options')
};
